import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import Reveal, { MaskedLine } from "@/components/Reveal";
import { API } from "@/lib/api";

export default function NewsArticle() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/media/${id}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen pt-48 pb-32" data-testid="article-not-found">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="font-display text-4xl text-ink">Article not found</h1>
          <p className="mt-4 text-ink/60">It may have been removed from the media section.</p>
          <Link to="/" data-testid="article-back-home" className="mt-8 inline-flex items-center gap-2 text-cobalt hover:gap-4 transition-[gap] duration-300 text-sm uppercase tracking-[0.25em]">
            <ArrowLeft size={15} /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return <div className="min-h-screen pt-48 pb-32" data-testid="article-loading" />;
  }

  const paragraphs = (post.article || post.body || "").split(/\n+/).filter(Boolean);

  return (
    <div data-testid="news-article-page">
      <header className="pt-40 pb-12 md:pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <Link to="/" data-testid="article-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ink/50 hover:text-cobalt transition-colors duration-300">
              <ArrowLeft size={14} /> All news
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-10 flex items-center gap-4 text-xs uppercase tracking-[0.35em] text-cobalt font-medium">
              <span className="h-px w-10 bg-cobalt/60" /> {post.date}
            </p>
          </Reveal>
          <h1 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-[1.15]" data-testid="article-title">
            <MaskedLine delay={0.15}>{post.title}</MaskedLine>
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-8 text-lg text-ink/70 leading-relaxed border-l-2 border-cobalt pl-6">
              {post.body}
            </p>
          </Reveal>
        </div>
      </header>

      <section className="pb-28" data-testid="article-body">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal delay={0.2}>
            <div className="space-y-6 text-ink/75 leading-relaxed text-base md:text-lg">
              {paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-16 border-t border-ink/10 pt-10 flex flex-wrap items-center justify-between gap-6">
              <p className="text-sm text-ink/50">Published by Axiom Computers · Meerut, Delhi NCR</p>
              <Link
                to="/contact"
                data-testid="article-contact-link"
                className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-6 py-3 text-sm tracking-wide transition-all duration-200 hover:bg-cobalt hover:scale-[0.98]"
              >
                Talk to us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
